import { useState, useCallback } from 'react'
import { z } from 'zod'
import type {
  ScenarioMode,
  ReplayMode,
  BetaMode,
  SimulateScenarioRequest,
} from '@/types/api'

// =============================================================================
// Zod Schemas
// =============================================================================

const historicalReplaySchema = z.object({
  episodeId: z.string().min(1, 'Please select a historical episode'),
  replayMode: z.enum(['PEAK_TO_TROUGH', 'FULL_PATH'] as const),
})

const btcCurveRegimeSchema = z.object({
  btcPricePath: z.array(z.number()).length(24, 'BTC price path must have 24 monthly points'),
  betaMode: z.enum(['NORMAL', 'STRESSED'] as const),
  selectedTemplateId: z.string().min(1, 'Please select a regime template'),
  templateOverrides: z.record(z.number()).optional(),
})

const customShocksSchema = z.object({
  assetShocks: z.record(z.number()).refine(
    (shocks) => Object.keys(shocks).length > 0,
    'Please specify at least one asset shock'
  ),
})

const scenarioFormSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('HISTORICAL_REPLAY'),
    historical: historicalReplaySchema,
  }),
  z.object({
    mode: z.literal('BTC_CURVE_REGIME'),
    btcCurve: btcCurveRegimeSchema,
  }),
  z.object({
    mode: z.literal('CUSTOM_SHOCKS'),
    custom: customShocksSchema,
  }),
])

// =============================================================================
// Types
// =============================================================================

type ScenarioFormData = z.infer<typeof scenarioFormSchema>

interface UseScenarioFormReturn {
  // Form state
  mode: ScenarioMode
  setMode: (mode: ScenarioMode) => void
  
  // Historical Replay state
  historicalForm: {
    episodeId: string
    replayMode: ReplayMode
    setEpisodeId: (id: string) => void
    setReplayMode: (mode: ReplayMode) => void
  }
  
  // BTC Curve state
  btcCurveForm: {
    btcPricePath: number[]
    betaMode: BetaMode
    selectedTemplateId: string
    templateOverrides: Record<string, number>
    setBtcPricePath: (path: number[]) => void
    setBetaMode: (mode: BetaMode) => void
    setSelectedTemplateId: (id: string) => void
    setTemplateOverride: (key: string, value: number | undefined) => void
    // Helper for simple mode
    setSimpleBtcPath: (startPct: number, endPct: number) => void
  }
  
  // Custom Shocks state
  customForm: {
    assetShocks: Record<string, number>
    setAssetShock: (assetId: string, shock: number) => void
    removeAssetShock: (assetId: string) => void
    resetAllShocks: () => void
  }
  
  // Form validation
  isValid: boolean
  errors: string[]
  
  // Submission
  buildRequest: () => SimulateScenarioRequest | null
}

// =============================================================================
// Helper: Generate linear BTC path
// =============================================================================

function generateLinearPath(startPct: number, endPct: number): number[] {
  const path: number[] = []
  for (let i = 0; i < 24; i++) {
    // Linear interpolation from start to end over 24 months
    const pct = startPct + (endPct - startPct) * (i / 23)
    path.push(Number(pct.toFixed(2)))
  }
  return path
}

// =============================================================================
// Hook
// =============================================================================

export function useScenarioForm(portfolioId: string): UseScenarioFormReturn {
  // Mode selection
  const [mode, setMode] = useState<ScenarioMode>('HISTORICAL_REPLAY')
  
  // Historical Replay state
  const [episodeId, setEpisodeId] = useState<string>('')
  const [replayMode, setReplayMode] = useState<ReplayMode>('PEAK_TO_TROUGH')
  
  // BTC Curve state
  const [btcPricePath, setBtcPricePath] = useState<number[]>(() => 
    generateLinearPath(0, -30) // Default: 0% to -30% over 24 months
  )
  const [betaMode, setBetaMode] = useState<BetaMode>('NORMAL')
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('')
  const [templateOverrides, setTemplateOverrides] = useState<Record<string, number>>({})
  
  // Custom Shocks state
  const [assetShocks, setAssetShocks] = useState<Record<string, number>>({})
  
  // =============================================================================
  // Actions
  // =============================================================================
  
  const setSimpleBtcPath = useCallback((startPct: number, endPct: number) => {
    setBtcPricePath(generateLinearPath(startPct, endPct))
  }, [])
  
  const setTemplateOverride = useCallback((key: string, value: number | undefined) => {
    setTemplateOverrides(prev => {
      if (value === undefined) {
        const { [key]: _, ...rest } = prev
        return rest
      }
      return { ...prev, [key]: value }
    })
  }, [])
  
  const setAssetShock = useCallback((assetId: string, shock: number) => {
    setAssetShocks(prev => ({ ...prev, [assetId]: shock }))
  }, [])
  
  const removeAssetShock = useCallback((assetId: string) => {
    setAssetShocks(prev => {
      const { [assetId]: _, ...rest } = prev
      return rest
    })
  }, [])
  
  const resetAllShocks = useCallback(() => {
    setAssetShocks({})
  }, [])
  
  // =============================================================================
  // Validation
  // =============================================================================
  
  const validate = useCallback((): { isValid: boolean; errors: string[] } => {
    const errors: string[] = []
    
    switch (mode) {
      case 'HISTORICAL_REPLAY':
        if (!episodeId) {
          errors.push('Please select a historical episode')
        }
        break
        
      case 'BTC_CURVE_REGIME':
        if (btcPricePath.length !== 24) {
          errors.push('BTC price path must have 24 monthly points')
        }
        if (!selectedTemplateId) {
          errors.push('Please select a regime template')
        }
        break
        
      case 'CUSTOM_SHOCKS':
        if (Object.keys(assetShocks).length === 0) {
          errors.push('Please specify at least one asset shock')
        }
        break
    }
    
    return { isValid: errors.length === 0, errors }
  }, [mode, episodeId, btcPricePath, selectedTemplateId, assetShocks])
  
  const { isValid, errors } = validate()
  
  // =============================================================================
  // Build Request
  // =============================================================================
  
  const buildRequest = useCallback((): SimulateScenarioRequest | null => {
    if (!isValid || !portfolioId) return null
    
    switch (mode) {
      case 'HISTORICAL_REPLAY':
        return {
          portfolioId,
          mode,
          historicalConfig: {
            episodeId: episodeId,
            replayType: replayMode,
          },
        }
        
      case 'BTC_CURVE_REGIME':
        return {
          portfolioId,
          mode,
          btcRegimeConfig: {
            btcPricePath: btcPricePath,
            regimeTemplateId: selectedTemplateId,
            betaMode: betaMode,
          },
        }
        
      case 'CUSTOM_SHOCKS':
        return {
          portfolioId,
          mode,
          customShocksConfig: {
            assetShocks: assetShocks,
          },
        }
        
      default:
        return null
    }
  }, [
    mode,
    portfolioId,
    episodeId,
    replayMode,
    btcPricePath,
    betaMode,
    selectedTemplateId,
    assetShocks,
    isValid,
  ])
  
  return {
    mode,
    setMode,
    historicalForm: {
      episodeId,
      replayMode,
      setEpisodeId,
      setReplayMode,
    },
    btcCurveForm: {
      btcPricePath,
      betaMode,
      selectedTemplateId,
      templateOverrides,
      setBtcPricePath,
      setBetaMode,
      setSelectedTemplateId,
      setTemplateOverride,
      setSimpleBtcPath,
    },
    customForm: {
      assetShocks,
      setAssetShock,
      removeAssetShock,
      resetAllShocks,
    },
    isValid,
    errors,
    buildRequest,
  }
}

export { scenarioFormSchema }
export type { ScenarioFormData }
